import { z } from 'zod';

import { MESSAGE } from '@/lib/constants';

// ─── Recurrence (shared by the add + edit job forms) ──────────────────────────
// Fields are string-y because they come straight off native <input>s; the
// modals map them to the API's `JobRecurrence` via `toJobRecurrence`.

export const recurrenceInputSchema = z.object({
  frequency: z.enum(['weekly', 'monthly', 'custom']),
  intervalCount: z.string().trim().optional(),
  daysOfWeek: z.array(z.number().int().min(0).max(6)).optional(),
  dayOfMonth: z.string().trim().optional(),
  customRule: z.string().trim().optional(),
  seriesEndDate: z.string().trim().optional(),
});

export type RecurrenceInput = z.infer<typeof recurrenceInputSchema>;

// Spread into any job form that offers a recurring schedule.
export const recurrenceFormFields = {
  repeats: z.boolean(),
  recurrence: recurrenceInputSchema.optional(),
};

export type RecurrenceFormValues = {
  repeats: boolean;
  recurrence?: RecurrenceInput;
};

// ─── Payment method (shared) ─────────────────────────────────────────────────
// Same six-value enum the API enforces on a job's `default_payment_method` and
// on a visit's `payment_method`.

export const PAYMENT_METHODS = [
  'zelle',
  'venmo',
  'stripe',
  'cash',
  'check',
  'other',
] as const;

export type PaymentMethodValue = (typeof PAYMENT_METHODS)[number];

// Job forms use `''` for "no default"; the modal maps that back to
// `default_payment_method: null` on submit. `undefined` = field untouched.
// The value is either a bare `PaymentMethodValue` (cash/check, or a type with
// no configured account) or a `payment_methods.id` (a specific configured
// account) — see `availablePaymentMethodOptions` in `helpers.ts`. It's a
// plain string rather than `z.enum(PAYMENT_METHODS)` because a UUID doesn't
// fit that enum; the actual option set is enforced by only ever rendering
// options from `availablePaymentMethodOptions`.
export const jobDefaultPaymentMethodField = z.string().optional();

// Cross-field recurrence validation — call from a schema's `superRefine`.
export function refineRecurrence(val: RecurrenceFormValues, ctx: z.RefinementCtx) {
  if (!val.repeats) return;

  if (!val.recurrence) {
    ctx.addIssue({ code: 'custom', path: ['recurrence'], message: MESSAGE.REQUIRED_FIELD });
    return;
  }

  const { frequency, daysOfWeek, dayOfMonth, customRule } = val.recurrence;

  if (frequency === 'weekly' && (!daysOfWeek || daysOfWeek.length === 0)) {
    ctx.addIssue({
      code: 'custom',
      path: ['recurrence', 'daysOfWeek'],
      message: MESSAGE.REQUIRED_FIELD,
    });
  }

  if (frequency === 'monthly' && !dayOfMonth) {
    ctx.addIssue({
      code: 'custom',
      path: ['recurrence', 'dayOfMonth'],
      message: MESSAGE.REQUIRED_FIELD,
    });
  }

  if (frequency === 'custom' && !customRule) {
    ctx.addIssue({
      code: 'custom',
      path: ['recurrence', 'customRule'],
      message: MESSAGE.REQUIRED_FIELD,
    });
  }
}

// ─── Edit job ────────────────────────────────────────────────────────────────
// Edits the logical job only — title, address, and the recurrence rule.
// Status / payment / schedule live on visits (`editVisitSchema`).

export const editJobSchema = z
  .object({
    title: z.string().trim().min(1, { message: MESSAGE.REQUIRED_FIELD }),
    serviceAddress: z.string().trim().optional(),
    defaultPaymentMethod: jobDefaultPaymentMethodField,
    ...recurrenceFormFields,
  })
  .superRefine(refineRecurrence);

export type EditJobInput = z.infer<typeof editJobSchema>;

// ─── Edit visit ──────────────────────────────────────────────────────────────
// One visit's schedule + status. Transitions are constrained server-side —
// callers surface the server's 400 rather than pre-validating.

export const editVisitSchema = z.object({
  status: z.enum(['draft', 'started', 'completed', 'cancelled']),
  scheduledDate: z.string().trim().optional(),
  startTime: z.string().trim().optional(),
  endTime: z.string().trim().optional(),
  totalAmount: z.string().trim().optional(),
});

export type EditVisitInput = z.infer<typeof editVisitSchema>;

// ─── Record payment ──────────────────────────────────────────────────────────
// Shared by the visit table and the job detail page. Flipping to `paid`
// is the only transition offered — `paid → unpaid` is never surfaced (the
// backend 400s it anyway).

export const recordPaymentSchema = z
  .object({
    amountCollected: z.string().trim().min(1, { message: MESSAGE.REQUIRED_FIELD }),
    // A bare `PaymentMethodValue` or a `payment_methods.id` — see
    // `jobDefaultPaymentMethodField` above.
    paymentMethod: z.string().trim().min(1, { message: MESSAGE.REQUIRED_FIELD }),
  })
  .superRefine((val, ctx) => {
    const n = Number(val.amountCollected);
    if (!Number.isFinite(n) || n < 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['amountCollected'],
        message: 'Enter a valid amount',
      });
    }
  });

export type RecordPaymentInput = z.infer<typeof recordPaymentSchema>;

// ─── Add job note ────────────────────────────────────────────────────────────

export const jobNoteSchema = z.object({
  body: z.string().trim().min(1, { message: MESSAGE.REQUIRED_FIELD }),
});

export type JobNoteInput = z.infer<typeof jobNoteSchema>;

// ─── Add job ─────────────────────────────────────────────────────────────────
// Form fields are camelCase and mostly strings (native <input> values); the
// modal maps them to the snake_case `CreateJobRequest` on submit. Conditional
// requirements (customer mode, recurrence shape) are enforced in `superRefine`.

export const addJobSchema = z
  .object({
    customerMode: z.enum(['existing', 'new']),
    customerUserId: z.string().trim().optional(),
    newCustomerName: z.string().trim().optional(),
    newCustomerEmail: z.string().trim().optional(),
    newCustomerPhone: z.string().trim().optional(),
    title: z.string().trim().min(1, { message: MESSAGE.REQUIRED_FIELD }),
    description: z.string().trim().optional(),
    serviceAddress: z.string().trim().optional(),
    startDate: z.string().trim().min(1, { message: MESSAGE.REQUIRED_FIELD }),
    endDate: z.string().trim().optional(),
    startTime: z.string().trim().optional(),
    endTime: z.string().trim().optional(),
    totalAmount: z.string().trim().optional(),
    defaultPaymentMethod: jobDefaultPaymentMethodField,
    ...recurrenceFormFields,
  })
  .superRefine((val, ctx) => {
    if (val.customerMode === 'existing' && !val.customerUserId) {
      ctx.addIssue({
        code: 'custom',
        path: ['customerUserId'],
        message: MESSAGE.REQUIRED_FIELD,
      });
    }

    if (val.customerMode === 'new') {
      // A job needs a customer with a linked account, which the API only
      // creates when the customer is given contact details — so name, email
      // and phone are all required on the inline "New customer…" path.
      if (!val.newCustomerName) {
        ctx.addIssue({
          code: 'custom',
          path: ['newCustomerName'],
          message: MESSAGE.REQUIRED_FIELD,
        });
      }
      if (!val.newCustomerEmail) {
        ctx.addIssue({
          code: 'custom',
          path: ['newCustomerEmail'],
          message: MESSAGE.REQUIRED_FIELD,
        });
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.newCustomerEmail)) {
        ctx.addIssue({
          code: 'custom',
          path: ['newCustomerEmail'],
          message: 'Enter a valid email address',
        });
      }
      if (!val.newCustomerPhone) {
        ctx.addIssue({
          code: 'custom',
          path: ['newCustomerPhone'],
          message: MESSAGE.REQUIRED_FIELD,
        });
      }
    }

    if (val.totalAmount) {
      const n = Number(val.totalAmount);
      if (!Number.isFinite(n) || n < 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['totalAmount'],
          message: 'Enter a valid amount',
        });
      }
    }

    if (val.startDate && val.endDate && val.endDate < val.startDate) {
      ctx.addIssue({
        code: 'custom',
        path: ['endDate'],
        message: 'End date must be on or after the start date',
      });
    }

    if (val.startTime && val.endTime) {
      const sameDay = !val.endDate || val.endDate === val.startDate;
      if (sameDay && val.endTime <= val.startTime) {
        ctx.addIssue({
          code: 'custom',
          path: ['endTime'],
          message: 'End time must be after the start time',
        });
      }
    }

    refineRecurrence(val, ctx);
  });

export type AddJobInput = z.infer<typeof addJobSchema>;

// ─── Assign employee to a visit ──────────────────────────────────────────────

export const assignVisitEmployeeSchema = z
  .object({
    employeeId: z.string().trim().min(1, { message: MESSAGE.REQUIRED_FIELD }),
    payAmount: z.string().trim().optional(),
  })
  .superRefine((val, ctx) => {
    if (!val.payAmount) return;
    const n = Number(val.payAmount);
    if (!Number.isFinite(n) || n < 0) {
      ctx.addIssue({ code: 'custom', path: ['payAmount'], message: 'Enter a valid amount' });
    }
  });

export type AssignVisitEmployeeInput = z.infer<typeof assignVisitEmployeeSchema>;
